/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */

import React, { useState, useRef, useMemo, useLayoutEffect, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Virtuoso } from 'react-virtuoso';
import TableHeaders from './TableHeaders';
import { TableRow } from './TableBody';

const SELECTION_COLUMN_TEMPLATE = 'minmax(2.5rem, 3rem)';

const VirtuosoRowGroup = React.forwardRef((props, ref) => <div {...props} ref={ref} role="rowgroup" />);
VirtuosoRowGroup.displayName = 'VirtuosoRowGroup';

const normalizeModifierState = (eventOrOptions = {}) => {
  if (!eventOrOptions) {
    return { shiftKey: false, metaKey: false, ctrlKey: false, forceSelect: false };
  }

  const nativeEvent = eventOrOptions.nativeEvent ?? eventOrOptions;

  return {
    shiftKey: Boolean(nativeEvent?.shiftKey),
    metaKey: Boolean(nativeEvent?.metaKey),
    ctrlKey: Boolean(nativeEvent?.ctrlKey),
    forceSelect: Boolean(eventOrOptions.forceSelect),
  };
};

export default function Table({ data, tableLayout, tableState, setTableState, highlightTransactionId, groupByColumn }) {
  // if header is less wide than 5px, then its contents has been hidden due to current breakpoint and the entire containing column needs to be hidden as well
  // the header which detects that its width is below 5px adds its id to the hiddenColumns state array in this component
  // HeaderCell and BodyColumn components check hiddenColumns if their id is there and if so, they add 'hidden' to their className
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [openDetailRows, setOpenDetailRows] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  const virtuosoRef = useRef(null); // Create ref for Virtuoso (used to handle scroll to view in Layout Effect)
  const highlightedRow = useRef(); // used to hightlight the row of highlightTransactionId (which is set by SearchResults)
  const containerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const gridTemplate = tableLayout?.columnParams || '';
  const [computedGridTemplate, setComputedGridTemplate] = useState('');
  const adjustedGridTemplate = useMemo(() => {
    if (!computedGridTemplate) return computedGridTemplate;
    return selectMode ? `${SELECTION_COLUMN_TEMPLATE} ${computedGridTemplate}` : computedGridTemplate;
  }, [computedGridTemplate, selectMode]);
  const virtuosoComponents = useMemo(() => ({ List: VirtuosoRowGroup }), []);

  function toggleDetailRow(value) {
    if (openDetailRows.includes(value)) {
      setOpenDetailRows(openDetailRows.filter((id) => id !== value));
    } else {
      setOpenDetailRows([...openDetailRows, value]);
    }
  }

  // Calculate visible rows by filtering out hidden rows
  const visibleRows = useMemo(
    () => data.filter((rowObject) => (!Object.prototype.hasOwnProperty.call(rowObject, 'isSummaryRow') || openDetailRows.includes(rowObject[groupByColumn]) ? true : rowObject.isSummaryRow)),
    [data, openDetailRows, groupByColumn],
  );

  const selectableRowIds = useMemo(() => visibleRows.map((row) => row?.id).filter((id) => id !== undefined && id !== null), [visibleRows]);
  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const isAllSelected = selectMode && selectableRowIds.length > 0 && selectableRowIds.every((id) => selectedSet.has(id));
  const isIndeterminate = selectMode && selectedItems.length > 0 && !isAllSelected;

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setSelectMode(false);
    setLastSelectedIndex(null);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const getRangeIds = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === null || fromIndex === undefined || toIndex === null || toIndex === undefined) return [];
      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex);
      const ids = [];
      for (let idx = start; idx <= end; idx += 1) {
        const rangeId = visibleRows[idx]?.id;
        if (rangeId !== undefined && rangeId !== null) ids.push(rangeId);
      }
      return ids;
    },
    [visibleRows],
  );

  const handleRowSelection = useCallback(
    (rowId, rowIndex, eventOrOptions = {}) => {
      if (rowId === undefined || rowId === null) return;
      const { shiftKey, forceSelect } = normalizeModifierState(eventOrOptions);
      setSelectMode(true);
      setSelectedItems((prev) => {
        if (shiftKey && lastSelectedIndex !== null && rowIndex !== null && rowIndex !== undefined) {
          const selectionSet = new Set(prev);
          getRangeIds(lastSelectedIndex, rowIndex).forEach((rangeId) => selectionSet.add(rangeId));
          return Array.from(selectionSet);
        }
        const selectionSet = new Set(prev);
        if (selectionSet.has(rowId) && !forceSelect) {
          selectionSet.delete(rowId);
        } else {
          selectionSet.add(rowId);
        }
        return Array.from(selectionSet);
      });
      setLastSelectedIndex(rowIndex);
    },
    [getRangeIds, lastSelectedIndex],
  );

  const handleHeaderToggle = useCallback(() => {
    if (!selectableRowIds.length) return;
    if (isAllSelected) {
      clearSelection();
      return;
    }
    setSelectMode(true);
    setSelectedItems(selectableRowIds);
    setLastSelectedIndex(null);
  }, [clearSelection, isAllSelected, selectableRowIds]);

  const handleTouchLongPressStart = useCallback(
    (event, rowId, rowIndex) => {
      if (selectMode || !rowId) return;
      const pointerType = event.pointerType || (event.touches ? 'touch' : '');
      if (pointerType !== 'touch') return;
      cancelLongPress();
      longPressTimerRef.current = window.setTimeout(() => {
        handleRowSelection(rowId, rowIndex, { forceSelect: true });
        cancelLongPress();
      }, 450);
    },
    [cancelLongPress, handleRowSelection, selectMode],
  );

  const getSelectionLabel = useCallback((row) => {
    if (!row) return 'Select transaction';
    const descriptor = row.description || row.otherParty || row.assetName || row.name || row.id || 'transaction';
    const dateValue = row.date;
    let formattedDate = '';
    if (dateValue) {
      let timestamp;
      if (dateValue instanceof Date) {
        timestamp = dateValue.getTime();
      } else if (typeof dateValue === 'number') {
        timestamp = dateValue;
      } else if (typeof dateValue === 'string') {
        const numeric = Number(dateValue);
        if (!Number.isNaN(numeric) && dateValue.trim() !== '') {
          timestamp = numeric;
        } else {
          const parsed = Date.parse(dateValue);
          if (!Number.isNaN(parsed)) timestamp = parsed;
        }
      }
      if (timestamp && !Number.isNaN(timestamp)) {
        formattedDate = ` on ${new Date(timestamp).toLocaleDateString('de', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
      }
    }
    return `Select transaction ${descriptor}${formattedDate}`;
  }, []);

  const rowContext = useMemo(() => ({ tableLayout, hiddenColumns, toggleDetailRow, openDetailRows, groupByColumn }), [tableLayout, hiddenColumns, toggleDetailRow, openDetailRows, groupByColumn]);

  // prepare the contents of TableRow to be passed as itemContent to Virtuoso
  const renderRow = useCallback(
    (index, item) => {
      const isHighlighted = item.id === highlightTransactionId;
      const isSelected = selectedSet.has(item.id);
      const paddingClasses = selectMode ? 'px-3' : 'pl-8 pr-3';
      const hoverClass = !selectMode ? 'hover:bg-gray-50' : '';
      const selectionClass = selectMode && isSelected ? 'bg-brandBlue-50/60' : '';
      return (
        <div
          key={item.id}
          id={`table-row-${index}`}
          ref={isHighlighted ? highlightedRow : undefined}
          role="row"
          aria-selected={selectMode ? isSelected : undefined}
          onPointerDown={(event) => handleTouchLongPressStart(event, item.id, index)}
          onPointerUp={cancelLongPress}
          onPointerLeave={cancelLongPress}
          onPointerCancel={cancelLongPress}
          className={`${paddingClasses} py-2 grid gap-3 items-center relative group ${item.isSimulated ? 'bg-brandViolet-50 italic' : ''} ${selectionClass} ${
            isHighlighted ? 'bg-brandYellow-100' : ''
          } ${item.isSummaryRow ? 'font-bold border-b border-gray-200' : 'font-medium border-b border-gray-200'} ${hoverClass}`}
          style={adjustedGridTemplate ? { gridTemplateColumns: adjustedGridTemplate } : undefined}
        >
          <TableRow
            index={index}
            rowObject={item}
            selectMode={selectMode}
            isSelected={isSelected}
            selectionLabel={getSelectionLabel(item)}
            onRowSelect={handleRowSelection}
            selectedTransactionIds={selectedItems}
            context={rowContext}
          />
        </div>
      );
    },
    [adjustedGridTemplate, cancelLongPress, getSelectionLabel, handleRowSelection, handleTouchLongPressStart, highlightTransactionId, rowContext, selectMode, selectedItems, selectedSet],
  );

  useEffect(() => {
    if (selectedItems.length === 0) {
      setSelectMode(false);
      setLastSelectedIndex(null);
    }
  }, [selectedItems.length]);

  useEffect(() => {
    if (!selectMode) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectMode, clearSelection]);

  useEffect(() => {
    const handleClearSelectionEvent = () => {
      clearSelection();
    };
    window.addEventListener('clearTableSelection', handleClearSelectionEvent);
    return () => {
      window.removeEventListener('clearTableSelection', handleClearSelectionEvent);
    };
  }, [clearSelection]);

  useEffect(
    () => () => {
      cancelLongPress();
    },
    [cancelLongPress],
  );

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node || typeof window === 'undefined') return undefined;

    const updateTemplate = () => {
      const appliedTemplate = window.getComputedStyle(node).gridTemplateColumns;
      setComputedGridTemplate(appliedTemplate);
    };

    updateTemplate();

    let resizeObserver;
    if (typeof window.ResizeObserver === 'function') {
      resizeObserver = new window.ResizeObserver(updateTemplate);
      resizeObserver.observe(node);
      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', updateTemplate);
    return () => {
      window.removeEventListener('resize', updateTemplate);
    };
  }, [gridTemplate]);

  useLayoutEffect(() => {
    if (highlightTransactionId && virtuosoRef.current) {
      const indexToScroll = visibleRows.findIndex((row) => row.id === highlightTransactionId);
      if (indexToScroll !== -1) {
        virtuosoRef.current.scrollToIndex(indexToScroll);
      }
    }
  }, [highlightTransactionId, visibleRows]);

  return (
    <section
      // eslint-disable-next-line max-len
      className="h-full min-h-0 self-stretch flex flex-col overflow-hidden relative overflow-y-hidden overflow-x-hidden"
      aria-label="transaction details"
      role="table"
    >
      {/* // main table container for fixed header and scrollable body */}
      <div ref={containerRef} className={`w-full h-full min-h-0 relative flex flex-col ${gridTemplate}`}>
        {selectMode && (
          <div className="flex items-center justify-between px-3 py-2 text-xs text-gray-700 bg-gray-50 border-b border-gray-200" role="toolbar" aria-label="Selection mode actions">
            <span className="font-semibold">
              {selectedItems.length}
              {' '}
              selected
            </span>
            <button
              type="button"
              className="text-brandBlue-600 hover:text-brandBlue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brandBlue-200 rounded px-2 py-1 text-xs font-medium"
              onClick={clearSelection}
            >
              Cancel
            </button>
          </div>
        )}

        <TableHeaders
          tableLayout={tableLayout}
          tableState={tableState}
          setTableState={setTableState}
          hiddenColumns={hiddenColumns}
          setHiddenColumns={setHiddenColumns}
          gridTemplateColumns={adjustedGridTemplate}
          selectMode={selectMode}
          selectionColumnProps={{
            checked: isAllSelected,
            indeterminate: isIndeterminate,
            onChange: handleHeaderToggle,
            disabled: selectableRowIds.length === 0,
          }}
        />
        {/* list */}
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: '100%', overflowX: 'hidden' }}
          className="flex-1 min-h-0 text-gray-500 bg-white text-xs xs:text-sm"
          data={visibleRows}
          itemContent={renderRow}
          components={virtuosoComponents}
        />
      </div>
    </section>
  );
}
Table.propTypes = {
  data: PropTypes.array.isRequired, // array of objects (each object should have exactly the same keys as there are columns in the table + an id)
  tableLayout: PropTypes.shape({
    columnParams: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  }).isRequired,
  tableState: PropTypes.object.isRequired, // contains column widths, sorting, filtering, etc., has to be passed to e.g. TableHeaders to be able to change it
  setTableState: PropTypes.func.isRequired,
  highlightTransactionId: PropTypes.string, // coming from search function, hightlights a transaction in the table
  groupByColumn: PropTypes.string, // determins which field of each row will be used to manage the open / closed state; default is 'assetId'
};
Table.defaultProps = {
  highlightTransactionId: null,
  groupByColumn: 'assetId',
};
